import axios from "axios";
import { api, apiUrl } from "../../../shared/services/service";
import {
  VesselCardEvaluationCreated,
  VesselCardEvaluationDownloadResponse,
  VesselCardEvaluationHistoryResponse,
  VesselCardEvaluationSubmission,
} from "../models/vessel-card-evaluation.models";

// Keeps multipart handling in the feature service and lets the browser supply its boundary.
export const submitVesselCardEvaluation = async (
  submission: VesselCardEvaluationSubmission,
  files: File[],
  signal: AbortSignal,
): Promise<VesselCardEvaluationCreated> => {
  const form = new FormData();
  form.append("body", JSON.stringify(submission));
  files.forEach((file) => form.append("files", file));
  const response = await api.post<VesselCardEvaluationCreated>(`${apiUrl}/pbi/vessel-card/evaluation-requests`, form, { signal });

  return response.data;
};

// Retrieves the latest 20 requests through the same authenticated vessel-card access boundary.
export const getVesselCardEvaluationHistory = async (imo: string, signal: AbortSignal): Promise<VesselCardEvaluationHistoryResponse> => {
  const response = await api.get<VesselCardEvaluationHistoryResponse>(`${apiUrl}/pbi/vessel-card/evaluation-requests`, { params: { imo }, signal });

  return response.data;
};

// Requests a short-lived download URL through OpsLink rather than exposing a storage key to the browser.
export const getVesselCardEvaluationDownload = async (
  requestId: string,
  attachmentId: string,
  signal: AbortSignal,
): Promise<VesselCardEvaluationDownloadResponse> => {
  const response = await api.get<VesselCardEvaluationDownloadResponse>(
    `${apiUrl}/pbi/vessel-card/evaluation-requests/${encodeURIComponent(requestId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { signal },
  );
  const url = new URL(response.data.url);

  if (url.protocol !== "https:" || url.username || url.password) {
    throw new Error("The attachment download link is invalid. Try again or contact support.");
  }

  return response.data;
};

export const getVesselCardEvaluationErrorMessage = (error: unknown, fallback: string): string => {

  if (axios.isAxiosError<{ error?: string }>(error)) {

    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    if (error.response?.status === 413) {
      return "The supporting files exceed the upload limit. Reduce the attachments and try again.";
    }

    if (error.response?.status === 403) {
      return "You do not have access to this evaluation request action.";
    }

    return fallback;
  }

  return error instanceof Error ? error.message : fallback;
};
